/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { useState, createRef, Fragment } from 'react';
import { jsx, css } from '@emotion/core';
import CheckMark from '../../images/check-mark.svg';
import api from 'lib/api';
import Present from 'lib/Present';
import { EmailT } from 'types/tcomb';
import { Fields } from 'lib/proforma';
import * as t from 'lib/t';
import MemberFormLogo from './MemberFormLogo';
import MemberFormSubmitBtn from './MemberFormSubmitBtn';
import MemberFormAltLink from './MemberFormAltLink';
import MemberFormErrorOutput from './MemberFormErrorOutput';
import utilCollectErrors from './utilCollectErrors';

const EmailField = EmailT.opt({
  type: 'email',
  attrs: { placeholder: 'Email' },
});

export function resetPassword(email: string, id: ID): Promise<any> {
  return api.fetchJson('/password', {
    method: 'POST',
    body: { member: { email }, communityId: id },
  });
}

export function MemberPasswordRequestForm({
  onLoginRequested,
  onSuccess,
  id,
}: {
  id: ID,
  onLoginRequested: () => any,
  onSuccess: ({ email: string }) => any,
}): Element<string> {
  const type = t.struct({
    email: EmailField,
  });
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState();
  const [value, setValue] = useState({});
  const myRef = createRef();

  function onSubmit() {
    const el = myRef.current;
    if (!el) return;
    const errs = el.validate().errors;
    if (Present.is(errs)) {
      setErrors();
      return;
    }

    setLoading(true);

    resetPassword(value.email, id)
      .then(() => {
        setErrors();
        onSuccess(value);
      })
      .catch((e) => {
        if (e.error) {
          setErrors([e.error]);
        } else if (Present.is(e.errors)) {
          setErrors(utilCollectErrors(e));
        } else if (Present.is(e)) {
          setErrors([JSON.stringify(e)]);
        } else {
          setErrors(['Unknown server error.']);
        }
        setLoading(false);
      });
  }

  return (
    <form
      data-testid="MemberPasswordRequestForm"
      onSubmit={(e) => e.preventDefault()}
      css={css(`
        label { display: none; }
      `)}
    >
      <MemberFormErrorOutput errors={errors} />
      <Fields
        ref={myRef}
        value={value}
        type={type}
        onChange={(v) => setValue(v)}
      />
      <div className="d-flex justify-content-between align-items-center">
        <MemberFormAltLink onClick={onLoginRequested}>
          Login with email
        </MemberFormAltLink>
        <MemberFormSubmitBtn
          width="150px"
          isLoading={isLoading}
          onClick={onSubmit}
        >
          Send instructions
        </MemberFormSubmitBtn>
      </div>
    </form>
  );
}

function ReadyScreen(props) {
  return (
    <Fragment>
      <MemberFormLogo id={props.id} />
      <h2 className="text-center">Reset password</h2>
      <MemberPasswordRequestForm {...props} />
    </Fragment>
  );
}

export function SuccessScreenInstructions(): Element<string> {
  return (
    <div className="text-center">
      <CheckMark style={{ fill: '#49bf49', height: 100 }} />
      <div className="text-success h4">
        We emailed you the instructions. Please check your email.
      </div>
    </div>
  );
}

function SuccessScreen({ onLoginRequested }) {
  return (
    <div data-testid="MemberPasswordRequestSuccess" className="text-center">
      <SuccessScreenInstructions />
      <div>
        <MemberFormAltLink
          onClick={() => {
            onLoginRequested();
          }}
        >
          Login <i className="fa fa-arrow-right" />
        </MemberFormAltLink>
      </div>
    </div>
  );
}

function MemberPasswordRequest({
  onLoginRequested,
  id,
}: {
  onLoginRequested: () => any,
  id: string | number,
}) {
  const [mode, setMode] = useState('READY');
  const isReady = mode === 'READY';

  return (
    <div data-testid="MemberPasswordRequest">
      {isReady ? (
        <ReadyScreen
          id={id}
          onSuccess={() => setMode('SUCCESS')}
          onLoginRequested={onLoginRequested}
        />
      ) : (
        <SuccessScreen onLoginRequested={onLoginRequested} />
      )}
    </div>
  );
}

export default (MemberPasswordRequest: any);
